/**
 * TimeTrackerView - Self-contained time tracking view
 *
 * Contains:
 * - My Work section (pinned projects + active sessions)
 * - Quote/Order/Operation selectors
 * - Job tracking with start/pause/complete actions
 *
 * @see docs/architecture/2026-01-08/NAVIGATION-RESTRUCTURE-MASTER-PLAN.md
 */

import { useState, useEffect, useCallback } from 'react';
import { useMsal } from '@azure/msal-react';
import { InteractionRequiredAuthError } from '@azure/msal-browser';
import QuoteSelector from '../QuoteSelector';
import OrderSelector from '../OrderSelector';
import OperationSelector from '../OperationSelector';
import JobTracker from '../JobTracker';
import JobDetailDrawer from '../JobDetailDrawer';
import CompleteModal from '../CompleteModal';
import MultiCompleteModal from '../MultiCompleteModal';
import PrinterSelectModal from '../PrinterSelectModal';
import ReprintModal from '../ReprintModal';
import ReprintRemakeModal from '../ReprintRemakeModal';
import ReworkCauseModal from '../ReworkCauseModal';
import PendingQuoteConfirmModal from '../PendingQuoteConfirmModal';
import PendingQuoteCompleteModal from '../PendingQuoteCompleteModal';
import MyWork from '../MyWork';
import TodaysSessions from '../TodaysSessions';
import ReworkStartModal from '../ReworkStartModal';
import PMInstantCompleteModal from '../PMInstantCompleteModal';
import SubmitProofModal from '../SubmitProofModal';
import { usePinnedQuotes } from '../MyPinnedQuotes';
import { PRINTERS, PRINT_MACH_OPERATION, COLOR_DEV_OPERATION } from '../../config/printers';

// REWORK operation codes that require cause capture
const REWORK_OPERATIONS = ['REWORK', 'REVISION'];

const API_BASE = import.meta.env.VITE_API_URL || '';
const apiScopes = [
  'api://2350508b-6d67-408a-9c55-c5040357d90d/b351e950-d526-4ee4-b95b-03f6ec89cd3d/access_as_user',
];

// Card styling - light theme
const card = "bg-white rounded-xl p-4 border border-gray-200 shadow-sm hover:shadow-lg hover:shadow-purple-500/10 transition-shadow";

export default function TimeTrackerView({ userEmail, isPM, isAdmin, onNavigateLegacy }) {
  const { instance, accounts } = useMsal();
  const { pinnedQuoteNumbers } = usePinnedQuotes();

  // =============================================================================
  // Selection State (mirrors WireframeApp) - with session persistence
  // =============================================================================
  const QUOTE_STORAGE_KEY = '3dcolor-selected-quote';
  const ORDER_STORAGE_KEY = '3dcolor-selected-order';

  const [selectedQuote, setSelectedQuote] = useState(() => {
    try {
      const stored = sessionStorage.getItem(QUOTE_STORAGE_KEY);
      return stored ? JSON.parse(stored) : null;
    } catch {
      return null;
    }
  });
  const [selectedOrder, setSelectedOrder] = useState(() => {
    try {
      const stored = sessionStorage.getItem(ORDER_STORAGE_KEY);
      return stored ? JSON.parse(stored) : null;
    } catch {
      return null;
    }
  });
  const [selectedOperation, setSelectedOperation] = useState(null);
  const [selectedJob, setSelectedJob] = useState(null);
  const [selectedJobIds, setSelectedJobIds] = useState([]);

  // Persist selections to sessionStorage
  useEffect(() => {
    if (selectedQuote) {
      sessionStorage.setItem(QUOTE_STORAGE_KEY, JSON.stringify(selectedQuote));
    } else {
      sessionStorage.removeItem(QUOTE_STORAGE_KEY);
    }
  }, [selectedQuote]);

  useEffect(() => {
    if (selectedOrder) {
      sessionStorage.setItem(ORDER_STORAGE_KEY, JSON.stringify(selectedOrder));
    } else {
      sessionStorage.removeItem(ORDER_STORAGE_KEY);
    }
  }, [selectedOrder]);

  // =============================================================================
  // Work Center State
  // =============================================================================
  const WORK_CENTER_STORAGE_KEY = '3dcolor-selected-workcenter';
  const [selectedWorkCenter, setSelectedWorkCenter] = useState(() => {
    return localStorage.getItem(WORK_CENTER_STORAGE_KEY) || null;
  });

  // Listen for work center changes
  useEffect(() => {
    const handleWorkCenterChanged = (event) => {
      setSelectedWorkCenter(event.detail);
    };
    window.addEventListener('workCenterChanged', handleWorkCenterChanged);
    return () => window.removeEventListener('workCenterChanged', handleWorkCenterChanged);
  }, []);

  // =============================================================================
  // Data State
  // =============================================================================
  const [jobs, setJobs] = useState([]);
  const [activeSessions, setActiveSessions] = useState({});
  const [allQuotes, setAllQuotes] = useState([]);

  // =============================================================================
  // Pending Quote Mode State
  // =============================================================================
  const [pendingQuoteMode, setPendingQuoteMode] = useState(false);
  const [pendingQuoteStarting, setPendingQuoteStarting] = useState(false);
  const [pendingQuoteConfirmModal, setPendingQuoteConfirmModal] = useState(false);
  const [pendingQuoteCompleteModal, setPendingQuoteCompleteModal] = useState({ open: false, ticket: null });

  // =============================================================================
  // Loading State
  // =============================================================================
  const [loading, setLoading] = useState(true);

  // =============================================================================
  // Modal State
  // =============================================================================
  const [completeModal, setCompleteModal] = useState({ open: false, job: null, autoQuantity: false });
  const [multiCompleteModal, setMultiCompleteModal] = useState({ open: false, jobs: [], autoQuantity: false });
  const [printerModal, setPrinterModal] = useState({ open: false, job: null, jobs: [], isMulti: false, printers: [], reprintType: null, reprintReason: null });
  const [reprintModal, setReprintModal] = useState({ open: false, job: null, jobs: [], isMulti: false, printCount: 0 });
  const [reprintRemakeModal, setReprintRemakeModal] = useState({ open: false, preselectedQuote: null, preselectedJobs: null });
  const [reworkCauseModal, setReworkCauseModal] = useState({ open: false, job: null, jobs: [], isMulti: false, operationType: 'REWORK' });
  const [reworkStartModal, setReworkStartModal] = useState({ open: false, task: null });
  const [submitProofModal, setSubmitProofModal] = useState({ open: false, mode: 'pdf-proof' });
  const [pmCompleteModal, setPmCompleteModal] = useState({ open: false, job: null, jobs: [] });

  // =============================================================================
  // Rework Queue State
  // =============================================================================
  const [reworkQueue, setReworkQueue] = useState([]);

  // =============================================================================
  // Operation Mapping (for autoQuantity)
  // =============================================================================
  const [operationMapping, setOperationMapping] = useState({});

  // =============================================================================
  // Routing State (for JobDetailDrawer)
  // =============================================================================
  const [routing, setRouting] = useState([]);
  const [routingLoading, setRoutingLoading] = useState(false);
  const [routingError, setRoutingError] = useState(null);
  const [selectedRoutingStep, setSelectedRoutingStep] = useState(null);

  // =============================================================================
  // Auth Helper
  // =============================================================================
  const acquireAccessToken = useCallback(async () => {
    if (accounts.length === 0) throw new Error('No logged in user.');
    const account = accounts[0];
    try {
      const tokenResponse = await instance.acquireTokenSilent({
        scopes: apiScopes,
        account,
      });
      sessionStorage.setItem('msal.backendToken', tokenResponse.accessToken);
      return tokenResponse.accessToken;
    } catch (error) {
      if (error instanceof InteractionRequiredAuthError) {
        console.warn('Session expired, redirecting to login...');
        await instance.loginRedirect({ scopes: apiScopes, account });
        return null;
      }
      throw error;
    }
  }, [instance, accounts]);

  const ensureToken = useCallback(async () => {
    let token = sessionStorage.getItem('msal.backendToken');
    if (!token) {
      token = await acquireAccessToken();
    }
    return token;
  }, [acquireAccessToken]);

  // Sync version for quick checks
  const ensureTokenSync = useCallback(() => {
    return sessionStorage.getItem('msal.backendToken');
  }, []);

  // =============================================================================
  // Data Fetching
  // =============================================================================

  // Load active sessions
  const loadActiveSessions = useCallback(async () => {
    try {
      const token = await ensureToken();
      if (!token) return;

      const res = await fetch(`${API_BASE}/api/me/time/active`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      setActiveSessions(data.sessions || {});
    } catch (err) {
      console.error('Error loading active sessions:', err);
    }
  }, [ensureToken]);

  // Load quotes (for MyWork enrichment only - QuoteSelector fetches its own)
  const loadQuotes = useCallback(async () => {
    try {
      const token = await ensureToken();
      if (!token) return;

      const res = await fetch(`${API_BASE}/api/quotes`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      setAllQuotes(data.quotes || []);
    } catch (err) {
      console.error('Error loading quotes:', err);
    }
  }, [ensureToken]);

  // Load jobs for selected order
  const loadJobs = useCallback(async (orderNumber) => {
    if (!orderNumber) {
      setJobs([]);
      return;
    }
    try {
      const token = await ensureToken();
      if (!token) return;

      const res = await fetch(`${API_BASE}/api/orders/${orderNumber}/jobs`, {
        headers: { Authorization: `Bearer ${token}` },
        credentials: 'include',
      });
      const data = await res.json();
      setJobs(Array.isArray(data.jobs) ? data.jobs : []);
    } catch (err) {
      console.error('Error loading jobs:', err);
      setJobs([]);
    }
  }, [ensureToken]);

  // Load operation mapping (for autoQuantity detection)
  const loadOperationMapping = useCallback(async () => {
    try {
      const token = await ensureToken();
      if (!token) return;

      const res = await fetch(`${API_BASE}/api/operations/mapping`, {
        headers: { Authorization: `Bearer ${token}` },
        credentials: 'include',
      });
      const data = await res.json();
      const rawMapping = data.mapping || {};

      // Transform from operationNumber keys to operationCode keys
      const byCode = {};
      for (const opNum of Object.keys(rawMapping)) {
        const entry = rawMapping[opNum];
        if (entry.operationCode) {
          byCode[entry.operationCode] = entry;
        }
      }
      setOperationMapping(byCode);
    } catch (err) {
      console.error('Error loading operation mapping:', err);
    }
  }, [ensureToken]);

  // Load rework queue for current work center
  const loadReworkQueue = useCallback(async (workCenter) => {
    if (!workCenter) {
      setReworkQueue([]);
      return;
    }
    try {
      const token = await ensureToken();
      if (!token) return;

      const res = await fetch(`${API_BASE}/api/rework-queue/${workCenter}`, {
        headers: { Authorization: `Bearer ${token}` },
        credentials: 'include',
      });
      if (!res.ok) {
        setReworkQueue([]);
        return;
      }
      const data = await res.json();
      setReworkQueue(data.items || []);
    } catch (err) {
      console.error('Error loading rework queue:', err);
      setReworkQueue([]);
    }
  }, [ensureToken]);

  // Check if operation has autoQuantity enabled
  const isAutoQuantityOperation = useCallback((operationCode) => {
    if (!operationCode) return false;
    const meta = operationMapping[operationCode];
    return meta?.autoQuantity === true;
  }, [operationMapping]);

  // =============================================================================
  // Effects
  // =============================================================================

  // Initial load
  useEffect(() => {
    async function init() {
      setLoading(true);
      await Promise.all([loadQuotes(), loadActiveSessions(), loadOperationMapping()]);
      setLoading(false);
    }
    init();
  }, [loadQuotes, loadActiveSessions, loadOperationMapping]);

  // Poll for active sessions
  useEffect(() => {
    const interval = setInterval(loadActiveSessions, 5000);
    return () => clearInterval(interval);
  }, [loadActiveSessions]);

  // Listen for time-updated events
  useEffect(() => {
    const handleTimeUpdated = () => loadActiveSessions();
    window.addEventListener('time-updated', handleTimeUpdated);
    return () => window.removeEventListener('time-updated', handleTimeUpdated);
  }, [loadActiveSessions]);

  // Load rework queue when work center changes
  useEffect(() => {
    loadReworkQueue(selectedWorkCenter);
  }, [selectedWorkCenter, loadReworkQueue]);

  // Clear order when quote changes
  useEffect(() => {
    setSelectedOrder(null);
  }, [selectedQuote?.quoteNumber]);

  // Load jobs when order changes
  useEffect(() => {
    if (selectedOrder?.orderNumber) {
      loadJobs(selectedOrder.orderNumber);
    } else {
      setJobs([]);
    }
    // Clear selections when order changes
    setSelectedJob(null);
    setSelectedJobIds([]);
    setSelectedRoutingStep(null);
  }, [selectedOrder?.orderNumber, loadJobs]);

  // Load routing when job is selected (for JobDetailDrawer)
  useEffect(() => {
    async function loadRouting() {
      if (!selectedJob?.jobNumber) {
        setRouting([]);
        setRoutingError(null);
        setRoutingLoading(false);
        return;
      }

      const token = sessionStorage.getItem('msal.backendToken');
      if (!token) {
        setRouting([]);
        setRoutingError('Missing backend token');
        setRoutingLoading(false);
        return;
      }

      setRoutingLoading(true);
      setRoutingError(null);

      try {
        const res = await fetch(
          `${API_BASE}/api/jobs/${selectedJob.jobNumber}/routing`,
          { headers: { Authorization: `Bearer ${token}` }, credentials: 'include' }
        );
        const data = await res.json();
        setRouting(Array.isArray(data?.routing) ? data.routing : []);
      } catch {
        setRouting([]);
        setRoutingError('Error loading routing');
      } finally {
        setRoutingLoading(false);
      }
    }
    loadRouting();
  }, [selectedJob]);

  // =============================================================================
  // Pending Quote Handlers
  // =============================================================================

  const handleStartPendingQuoteClick = useCallback(() => {
    if (!selectedQuote || !selectedOperation) {
      alert('Please select a quote and operation first.');
      return;
    }
    setPendingQuoteConfirmModal(true);
  }, [selectedQuote, selectedOperation]);

  const handleConfirmStartPendingQuote = useCallback(async () => {
    const token = ensureTokenSync();
    if (!token) return;

    setPendingQuoteStarting(true);
    try {
      const res = await fetch(`${API_BASE}/api/quotes/${selectedQuote.quoteNumber}/time/start`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          operationCode: selectedOperation.operationCode,
          projectName: selectedQuote.user_Text4 || `Quote #${selectedQuote.quoteNumber}`,
          workCenter: selectedOperation.workCenter || null,
        }),
      });

      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.error || 'Failed to start time tracking');
      }

      console.log('✅ Started pending quote time');
      setPendingQuoteConfirmModal(false);
      setPendingQuoteMode(false);
      window.dispatchEvent(new CustomEvent('time-updated'));
    } catch (err) {
      console.error('Error starting pending quote time:', err);
      alert(err.message || 'Failed to start time tracking');
    } finally {
      setPendingQuoteStarting(false);
    }
  }, [selectedQuote, selectedOperation, ensureTokenSync]);

  // =============================================================================
  // Job Handlers
  // =============================================================================

  const handleSelectQuote = useCallback((quote) => {
    setSelectedQuote(quote);
  }, []);

  const handleSelectOrder = useCallback((order) => {
    setSelectedOrder(order);
  }, []);

  const handleSelectOperation = useCallback((operation) => {
    setSelectedOperation(operation);
    // Clear routing step selection when operation changes from the selector
    setSelectedRoutingStep(null);
  }, []);

  // Handle routing step selection from JobDetailDrawer
  const handleRoutingStepSelect = useCallback((step) => {
    setSelectedRoutingStep(step);

    // Update the selected operation to match the routing step
    setSelectedOperation({
      operation: step.operation || step.operationCode,
      operationName: step.operation || step.operationCode,
      operationCode: step.operationCode,
      workCenter: step.workCenter,
      numberStep: step.stepNumber,
    });
  }, []);

  // Handle job click - toggles job selection for drawer
  const handleJobClick = useCallback((job) => {
    if (selectedJob?.id === job.id) {
      // Clicking same job closes drawer
      setSelectedJob(null);
      setSelectedRoutingStep(null);
    } else {
      // Clicking different job opens drawer for that job
      setSelectedJob(job);
      setSelectedRoutingStep(null);
    }
  }, [selectedJob]);

  // Start timer
  const handleStartJob = useCallback(async (job) => {
    if (!selectedOperation) {
      alert('Please select an operation first.');
      return;
    }

    // Check if PRINT-MACH or COLOR DEV operation - needs printer selection
    const needsPrinter = selectedOperation.operationCode === PRINT_MACH_OPERATION ||
                         selectedOperation.operationCode === COLOR_DEV_OPERATION;
    if (needsPrinter) {
      // Use printers from config, not API
      setPrinterModal({ open: true, job, printers: PRINTERS });
      return;
    }

    // Check if REWORK or REVISION operation - needs cause capture
    if (REWORK_OPERATIONS.includes(selectedOperation.operationCode)) {
      setReworkCauseModal({
        open: true,
        job,
        jobs: [],
        isMulti: false,
        operationType: selectedOperation.operationCode,
      });
      return;
    }

    await startJobWithPrinter(job, null);
  }, [selectedOperation]);

  // Start job with printer (and optional rework cause/type)
  const startJobWithPrinter = useCallback(async (job, printer, reworkCause = null, reworkNotes = null, reworkType = null) => {
    try {
      const token = await ensureToken();
      if (!token) return;

      // Use the correct endpoint: /api/jobs/:jobId/time/start
      const res = await fetch(`${API_BASE}/api/jobs/${encodeURIComponent(job.id)}/time/start`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          operationCode: selectedOperation.operationCode,
          workCenter: selectedOperation.workCenter,
          numberStep: selectedOperation.numberStep || null,
          quoteNumber: selectedOrder?.quoteNumber || selectedQuote?.quoteNumber,
          orderNumber: selectedOrder?.orderNumber,
          projectName: selectedOrder?.projectName || selectedQuote?.user_Text4 || `Job ${job.jobNumber}`,
          jobNumber: job.jobNumber,
          jobName: job.name,
          qty: job.qty,
          printer: printer ? { id: printer.id, name: printer.name } : null,
          reworkCause,
          reworkNotes,
          reworkType,
        }),
      });

      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.error || 'Failed to start timer');
      }

      window.dispatchEvent(new CustomEvent('time-updated'));
    } catch (err) {
      console.error('Error starting job:', err);
      alert(err.message || 'Failed to start timer');
    }
  }, [ensureToken, selectedQuote, selectedOrder, selectedOperation]);

  // Start multiple jobs at once (group start)
  const handleStartGroupJobs = useCallback(async (jobsToStart) => {
    if (!selectedOperation) {
      alert('Please select an operation first.');
      return;
    }

    if (!jobsToStart || jobsToStart.length === 0) {
      return;
    }

    // Check if operation needs printer selection
    const needsPrinter = selectedOperation.operationCode === PRINT_MACH_OPERATION ||
                         selectedOperation.operationCode === COLOR_DEV_OPERATION;

    if (needsPrinter) {
      try {
        const token = await ensureToken();
        if (!token) return;

        // For PRINT-MACH operations, check print history for all jobs
        if (selectedOperation.operationCode === PRINT_MACH_OPERATION) {
          // Check print history for each job
          const historyChecks = await Promise.all(
            jobsToStart.map(async (job) => {
              try {
                const historyRes = await fetch(`${API_BASE}/api/jobs/${encodeURIComponent(job.id)}/print-history`, {
                  headers: { Authorization: `Bearer ${token}` },
                });
                if (historyRes.ok) {
                  const data = await historyRes.json();
                  return { job, wasPreviouslyPrinted: data.wasPreviouslyPrinted, printCount: data.printCount || 0 };
                }
              } catch (e) {
                console.error('Error checking print history for job', job.id, e);
              }
              return { job, wasPreviouslyPrinted: false, printCount: 0 };
            })
          );

          // If any job was previously printed, show reprint modal
          const previouslyPrinted = historyChecks.filter(h => h.wasPreviouslyPrinted);
          if (previouslyPrinted.length > 0) {
            const maxPrintCount = Math.max(...previouslyPrinted.map(h => h.printCount));
            setReprintModal({
              open: true,
              job: null,
              jobs: jobsToStart,
              isMulti: true,
              printCount: maxPrintCount,
            });
            return;
          }
        }

        // No previous prints (or COLOR DEV), go directly to printer selection
        setPrinterModal({
          open: true,
          job: null,
          jobs: jobsToStart,
          isMulti: true,
          printers: PRINTERS,
          reprintType: null,
          reprintReason: null,
        });
        return;
      } catch (err) {
        console.error('Error checking print history:', err);
        // Continue to printer selection if check fails
        setPrinterModal({
          open: true,
          job: null,
          jobs: jobsToStart,
          isMulti: true,
          printers: PRINTERS,
          reprintType: null,
          reprintReason: null,
        });
        return;
      }
    }

    // Check if REWORK or REVISION operation - needs cause capture
    if (REWORK_OPERATIONS.includes(selectedOperation.operationCode)) {
      setReworkCauseModal({
        open: true,
        job: null,
        jobs: jobsToStart,
        isMulti: true,
        operationType: selectedOperation.operationCode,
      });
      return;
    }

    // For non-printer operations, start directly
    await startGroupJobsDirectly(jobsToStart, null, null, null);
  }, [ensureToken, selectedQuote, selectedOrder, selectedOperation]);

  // Helper function to actually start the group jobs
  const startGroupJobsDirectly = useCallback(async (jobsToStart, printer, reprintType, reprintReason, reworkCause = null, reworkNotes = null, reworkType = null) => {
    try {
      const token = await ensureToken();
      if (!token) return;

      // Generate a shared sessionGroupId for all jobs in this group
      let sessionGroupId = null;
      if (jobsToStart.length > 1) {
        if (window.crypto && window.crypto.randomUUID) {
          sessionGroupId = window.crypto.randomUUID();
        } else {
          sessionGroupId = `grp-${Date.now()}-${Math.random().toString(16).slice(2)}`;
        }
        console.log('Generated sessionGroupId for group start:', sessionGroupId);
      }

      // Start all jobs in parallel with the shared sessionGroupId
      const startPromises = jobsToStart.map(async (job) => {
        const res = await fetch(`${API_BASE}/api/jobs/${encodeURIComponent(job.id)}/time/start`, {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            operationCode: selectedOperation.operationCode,
            workCenter: selectedOperation.workCenter,
            numberStep: selectedOperation.numberStep || null,
            quoteNumber: selectedOrder?.quoteNumber || selectedQuote?.quoteNumber,
            orderNumber: selectedOrder?.orderNumber,
            projectName: selectedOrder?.projectName || selectedQuote?.user_Text4 || `Job ${job.jobNumber}`,
            jobNumber: job.jobNumber,
            jobName: job.name,
            qty: job.qty,
            printer: printer ? { id: printer.id, name: printer.name } : null,
            sessionGroupId,
            reprintType: reprintType,
            reprintReason: reprintReason,
            reworkCause,
            reworkNotes,
            reworkType,
          }),
        });

        if (!res.ok) {
          const data = await res.json().catch(() => ({}));
          throw new Error(data.error || `Failed to start job ${job.jobNumber}`);
        }

        return job.jobNumber;
      });

      const results = await Promise.allSettled(startPromises);
      const succeeded = results.filter(r => r.status === 'fulfilled').length;
      const failed = results.filter(r => r.status === 'rejected');

      if (failed.length > 0) {
        console.error('Some jobs failed to start:', failed);
        alert(`Started ${succeeded}/${jobsToStart.length} jobs. ${failed.length} failed.`);
      }

      // Clear selection after group start
      setSelectedJobIds([]);

      window.dispatchEvent(new CustomEvent('time-updated'));
    } catch (err) {
      console.error('Error starting group jobs:', err);
      alert(err.message || 'Failed to start jobs');
    }
  }, [ensureToken, selectedQuote, selectedOrder, selectedOperation]);

  // Pause timer - accepts job object or job ID string
  const handlePauseJob = useCallback(async (jobOrId) => {
    const jobId = typeof jobOrId === 'object' ? jobOrId.id : jobOrId;
    try {
      const token = await ensureToken();
      if (!token) return;

      await fetch(`${API_BASE}/api/jobs/${encodeURIComponent(jobId)}/time/pause`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      window.dispatchEvent(new CustomEvent('time-updated'));
    } catch (err) {
      console.error('Error pausing job:', err);
    }
  }, [ensureToken]);

  // Resume timer - accepts job object or job ID string
  const handleResumeJob = useCallback(async (jobOrId) => {
    const jobId = typeof jobOrId === 'object' ? jobOrId.id : jobOrId;
    try {
      const token = await ensureToken();
      if (!token) return;

      await fetch(`${API_BASE}/api/jobs/${encodeURIComponent(jobId)}/time/resume`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      window.dispatchEvent(new CustomEvent('time-updated'));
    } catch (err) {
      console.error('Error resuming job:', err);
    }
  }, [ensureToken]);

  const handleCompleteGroupJobs = useCallback((groupJobs) => {
    if (!groupJobs || groupJobs.length === 0) return;
    const autoQty = isAutoQuantityOperation(selectedOperation?.operationCode);
    setMultiCompleteModal({ open: true, jobs: groupJobs, autoQuantity: autoQty });
  }, [selectedOperation, isAutoQuantityOperation]);

  // Complete timer - opens modal
  const handleCompleteJob = useCallback((job) => {
    // Check if this is a pending quote job
    const isPendingQuote = job?.id?.startsWith?.('PENDING-');
    if (isPendingQuote) {
      const ticket = activeSessions[job.id];
      setPendingQuoteCompleteModal({ open: true, ticket: { ...ticket, id: job.id } });
      return;
    }

    // Check if this operation has autoQuantity enabled
    const autoQty = isAutoQuantityOperation(selectedOperation?.operationCode);

    // Check if this job is part of a group session
    const jobSession = activeSessions[job.id];
    const sessionGroupId = jobSession?.sessionGroupId;

    // Find all jobs that are part of the same session group
    let groupedJobs = [];
    if (sessionGroupId) {
      groupedJobs = jobs.filter((j) => {
        const s = activeSessions[j.id];
        return s?.sessionGroupId === sessionGroupId && (s?.state === 'running' || s?.state === 'paused');
      });
    }

    // Also consider manual multi-selection
    const selectedWithActive = selectedJobIds.filter((id) => {
      const sess = activeSessions[id];
      return sess && (sess.state === 'running' || sess.state === 'paused');
    });

    // Use group session jobs if available, otherwise fall back to manual selection
    const isGroupSession = groupedJobs.length > 1;
    const isMulti = isGroupSession || selectedWithActive.length > 1;

    if (isMulti) {
      const jobsForModal = isGroupSession
        ? groupedJobs
        : jobs.filter((j) => selectedWithActive.includes(j.id));
      setMultiCompleteModal({ open: true, jobs: jobsForModal, autoQuantity: autoQty });
    } else {
      setCompleteModal({ open: true, job, autoQuantity: autoQty });
    }
  }, [selectedJobIds, activeSessions, jobs, selectedOperation, isAutoQuantityOperation]);

  // Handle reprint modal response
  // Note: Reprint info is captured at START time in the backend, not separately.
  // This modal is for acknowledgment before completing - just proceed to complete.
  const handleReprintResponse = useCallback(async (isReprint, reprintType, reprintReason) => {
    const { job } = reprintModal;
    if (!job) return;

    // Note: reprintType/reprintReason are captured at START time in the backend.
    // This modal flow is for user confirmation before completing.
    // We log the info but don't make a separate API call.
    if (isReprint) {
      console.log('Reprint acknowledged:', { reprintType, reprintReason, jobId: job.id });
    }

    // Check if this operation has autoQuantity enabled
    const autoQty = isAutoQuantityOperation(selectedOperation?.operationCode);

    setReprintModal({ open: false, job: null });
    setCompleteModal({ open: true, job, autoQuantity: autoQty });
  }, [reprintModal, selectedOperation, isAutoQuantityOperation]);

  // Handle rework cause modal submission
  const handleReworkCauseSubmit = useCallback(async ({ reworkType, cause, notes }) => {
    const { job, jobs, isMulti } = reworkCauseModal;
    setReworkCauseModal({ open: false, job: null, jobs: [], isMulti: false, operationType: 'REWORK' });

    if (isMulti && jobs.length > 0) {
      // Multi-job start with rework cause - use group start helper
      await startGroupJobsDirectly(jobs, null, null, null, cause, notes, reworkType);
    } else if (job) {
      // Single job start with rework cause
      await startJobWithPrinter(job, null, cause, notes, reworkType);
    }
  }, [reworkCauseModal, startJobWithPrinter, startGroupJobsDirectly]);

  // Handle rework cause modal cancel
  const handleReworkCauseCancel = useCallback(() => {
    setReworkCauseModal({ open: false, job: null, jobs: [], isMulti: false, operationType: 'REWORK' });
  }, []);

  // Handle instant complete for PM operations (PDF APPROVAL, etc.) - shows confirmation modal
  const handleInstantComplete = useCallback((job, jobs = []) => {
    if (!selectedOperation) {
      alert('Please select an operation first.');
      return;
    }

    // Support both single job and multiple jobs
    if (jobs && jobs.length > 0) {
      // Multi-job completion
      setPmCompleteModal({ open: true, job: null, jobs });
    } else if (job) {
      // Single job completion
      setPmCompleteModal({ open: true, job, jobs: [] });
    }
  }, [selectedOperation]);

  // Cancel PM complete modal
  const handleCancelPmComplete = useCallback(() => {
    setPmCompleteModal({ open: false, job: null, jobs: [] });
  }, []);

  // Confirm PM instant complete - actually calls the API
  const handleConfirmPmComplete = useCallback(async () => {
    const { job, jobs } = pmCompleteModal;
    const jobList = jobs && jobs.length > 0 ? jobs : (job ? [job] : []);

    if (jobList.length === 0 || !selectedOperation) {
      setPmCompleteModal({ open: false, job: null, jobs: [] });
      return;
    }

    try {
      const token = await ensureToken();
      if (!token) return;

      // Process all jobs
      const results = await Promise.allSettled(
        jobList.map(async (j) => {
          const res = await fetch(`${API_BASE}/api/jobs/${encodeURIComponent(j.id)}/time/instant-complete`, {
            method: 'POST',
            headers: {
              Authorization: `Bearer ${token}`,
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              operationCode: selectedOperation.operationCode,
              workCenter: selectedOperation.workCenter || 'PM',
              numberStep: selectedOperation.numberStep,
              jobNumber: j.jobNumber,
              jobName: j.name,
              qty: j.qty,
              orderNumber: selectedOrder?.orderNumber || '',
              quoteNumber: selectedOrder?.quoteNumber || selectedQuote?.quoteNumber || null,
              projectName: selectedOrder?.projectName || selectedQuote?.user_Text4 || null,
            }),
          });

          if (!res.ok) {
            const data = await res.json().catch(() => ({}));
            throw new Error(data.error || `Failed for job ${j.jobNumber}`);
          }

          return j.jobNumber;
        })
      );

      const succeeded = results.filter(r => r.status === 'fulfilled').length;
      const failed = results.filter(r => r.status === 'rejected');

      if (failed.length > 0) {
        console.error('Some jobs failed:', failed);
        alert(`Completed ${succeeded}/${jobList.length} jobs. ${failed.length} failed.`);
      } else {
        console.log(`Successfully completed ${succeeded} job(s)`);
      }

      // Clear selection after completing
      setSelectedJobIds([]);

      window.dispatchEvent(new CustomEvent('time-updated'));
    } catch (err) {
      console.error('Error with instant complete:', err);
      alert('Failed to mark job(s) as complete.');
    } finally {
      setPmCompleteModal({ open: false, job: null, jobs: [] });
    }
  }, [pmCompleteModal, ensureToken, selectedOperation, selectedOrder, selectedQuote]);

  // Handle complete confirmation
  const handleConfirmComplete = useCallback(async (quantityCompleted) => {
    const { job } = completeModal;
    if (!job) return;

    try {
      const token = await ensureToken();
      if (!token) return;

      await fetch(`${API_BASE}/api/jobs/${encodeURIComponent(job.id)}/time/complete`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ piecesFinished: quantityCompleted }),
      });

      setCompleteModal({ open: false, job: null, autoQuantity: false });
      window.dispatchEvent(new CustomEvent('time-updated'));
    } catch (err) {
      console.error('Error completing job:', err);
      alert('Failed to complete job');
    }
  }, [completeModal, ensureToken]);

  // Handle multi-complete confirmation
  const handleConfirmMultiComplete = useCallback(async (quantities) => {
    const { jobs: jobsToComplete } = multiCompleteModal;
    if (!jobsToComplete.length) return;

    try {
      const token = await ensureToken();
      if (!token) return;

      await Promise.all(
        jobsToComplete.map((job) =>
          fetch(`${API_BASE}/api/jobs/${encodeURIComponent(job.id)}/time/complete`, {
            method: 'POST',
            headers: {
              Authorization: `Bearer ${token}`,
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({ piecesFinished: quantities[job.id] || 0 }),
          })
        )
      );

      setMultiCompleteModal({ open: false, jobs: [], autoQuantity: false });
      setSelectedJobIds([]);
      window.dispatchEvent(new CustomEvent('time-updated'));
    } catch (err) {
      console.error('Error completing jobs:', err);
      alert('Failed to complete some jobs');
    }
  }, [multiCompleteModal, ensureToken]);

  // Handle pending quote complete
  const handlePendingQuoteComplete = useCallback(async ({ piecesFinished, useAutoQuantity }) => {
    const { ticket } = pendingQuoteCompleteModal;
    if (!ticket) return;

    try {
      const token = await ensureToken();
      if (!token) return;

      // Pending quote tickets use PENDING-quoteNumber format, complete via jobs endpoint
      await fetch(`${API_BASE}/api/jobs/${encodeURIComponent(ticket.id)}/time/complete`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          quantityCompleted: useAutoQuantity ? null : piecesFinished,
          useAutoQuantity,
        }),
      });

      setPendingQuoteCompleteModal({ open: false, ticket: null });
      window.dispatchEvent(new CustomEvent('time-updated'));
    } catch (err) {
      console.error('Error completing pending quote:', err);
      alert('Failed to complete');
    }
  }, [pendingQuoteCompleteModal, ensureToken]);

  // Job selection change (multi-select)
  const handleSelectionChange = useCallback((newSelection) => {
    setSelectedJobIds(newSelection);
  }, []);

  // Navigate to job from MyWork
  const handleNavigateToJob = useCallback(async ({ quoteNumber, orderNumber }) => {
    if (!quoteNumber) return;

    const isSameQuote = selectedQuote?.quoteNumber?.toString() === quoteNumber?.toString();

    if (!isSameQuote) {
      if (orderNumber) {
        sessionStorage.setItem('3dcolor-pending-order', orderNumber);
      }
      setSelectedQuote({ quoteNumber });
    } else if (orderNumber) {
      // Same quote - directly fetch and set the order
      const token = sessionStorage.getItem('msal.backendToken');
      if (token) {
        try {
          const res = await fetch(
            `${API_BASE}/api/orders?quoteNumber=${encodeURIComponent(quoteNumber)}`,
            { headers: { Authorization: `Bearer ${token}` }, credentials: 'include' }
          );
          if (res.ok) {
            const data = await res.json();
            const orders = data.orders || [];
            const targetOrder = orders.find((o) => String(o.orderNumber) === String(orderNumber));
            if (targetOrder) {
              setSelectedOrder(targetOrder);
            }
          }
        } catch (err) {
          console.error('Error fetching order for navigation:', err);
        }
      }
    }
  }, [selectedQuote]);

  // Navigate to Session History (under time-tracking section, hidden from nav)
  const handleViewSessionHistory = useCallback(() => {
    window.location.hash = '#/time-tracking/history';
  }, []);

  // =============================================================================
  // Render
  // =============================================================================

  // Show loading state
  if (loading) {
    return (
      <div className="min-h-full bg-slate-200 text-gray-900">
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-full bg-slate-200 text-gray-900">
      <div className="max-w-7xl mx-auto px-4 py-6 flex flex-col gap-4">

        {/* MY WORK - Unified section for pinned projects and active sessions */}
        <MyWork
          allQuotes={allQuotes}
          activeSessions={activeSessions}
          onSelectQuote={handleSelectQuote}
          onNavigateToJob={handleNavigateToJob}
          onPauseJob={handlePauseJob}
          onResumeJob={handleResumeJob}
          onCompleteJob={handleCompleteJob}
          pendingTicketsReady={0}
          onAssignPendingTickets={() => {}}
          reworkQueue={reworkQueue}
          onStartReworkTask={(task) => setReworkStartModal({ open: true, task })}
        />

        {/* PENDING QUOTE MODE BANNER */}
        {pendingQuoteMode && (
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-amber-100 rounded-full flex items-center justify-center">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-amber-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div>
                <div className="font-semibold text-amber-800">Pending Quote Mode</div>
                <div className="text-sm text-amber-700">
                  Track time against a quote that doesn't have orders yet. Select a quote and operation, then click "Start Timer".
                </div>
              </div>
            </div>
            <div className="flex items-center gap-3">
              {selectedQuote && selectedOperation && (
                <button
                  onClick={handleStartPendingQuoteClick}
                  disabled={pendingQuoteStarting}
                  className="px-4 py-2 bg-gradient-to-r from-purple-600 to-orange-500 hover:from-purple-700 hover:to-orange-600 disabled:from-gray-400 disabled:to-gray-400 disabled:cursor-not-allowed text-white rounded-lg font-medium shadow-md transition-all flex items-center gap-2"
                >
                  {pendingQuoteStarting ? (
                    <>
                      <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                      </svg>
                      Starting...
                    </>
                  ) : (
                    <>
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      Start Timer
                    </>
                  )}
                </button>
              )}
              <button
                onClick={() => setPendingQuoteMode(false)}
                className="px-3 py-2 bg-slate-100 hover:bg-gray-200 text-gray-700 rounded-lg text-sm transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        )}

        {/* CONTEXT BAR - Shows current selection */}
        {(selectedQuote || selectedOrder || selectedOperation) && (
          <div className="flex items-center gap-3 flex-wrap">
            {/* Quote Chip */}
            {selectedQuote && (
              <div className="flex items-center gap-2 px-3 py-1.5 bg-white rounded-md border border-gray-200 shadow-sm">
                <span className="text-gray-500 text-xs font-medium">Quote:</span>
                <span className="text-gray-900 text-sm font-semibold">
                  {selectedQuote.quoteNumber}
                </span>
                {selectedQuote.projectName && (
                  <span className="text-gray-500 text-xs truncate max-w-[150px]">
                    {selectedQuote.projectName}
                  </span>
                )}
                <button
                  onClick={() => {
                    setSelectedQuote(null);
                    setSelectedOrder(null);
                  }}
                  className="ml-1 text-gray-400 hover:text-gray-600"
                  title="Clear quote"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            )}

            {/* Order Chip */}
            {selectedOrder && (
              <div className="flex items-center gap-2 px-3 py-1.5 bg-white rounded-md border border-gray-200 shadow-sm">
                <span className="text-gray-500 text-xs font-medium">Order:</span>
                <span className="text-gray-900 text-sm font-semibold">
                  {selectedOrder.orderNumber}
                </span>
                {selectedOrder.projectName && (
                  <span className="text-gray-500 text-xs truncate max-w-[150px]">
                    {selectedOrder.projectName}
                  </span>
                )}
                <button
                  onClick={() => setSelectedOrder(null)}
                  className="ml-1 text-gray-400 hover:text-gray-600"
                  title="Clear order"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            )}

            {/* Operation Chip */}
            {selectedOperation && (
              <div className="flex items-center gap-2 px-3 py-1.5 bg-purple-50 rounded-md border border-purple-200 shadow-sm">
                <span className="text-purple-600 text-xs font-medium">Operation:</span>
                <span className="text-purple-900 text-sm font-semibold">
                  {selectedOperation.operationCode || selectedOperation.operationName}
                </span>
                {selectedOperation.workCenter && (
                  <span className="text-purple-500 text-xs">
                    ({selectedOperation.workCenter})
                  </span>
                )}
              </div>
            )}

            {/* Work Order Button */}
            {selectedOrder && (
              selectedOrder.workOrderUrl ? (
                <a
                  href={selectedOrder.workOrderUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 px-3 py-1.5 bg-blue-600 hover:bg-blue-700 rounded-md text-white text-sm font-medium transition-colors shadow-sm"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  Work Order
                </a>
              ) : (
                <div className="flex items-center gap-2 px-3 py-1.5 bg-gray-100 rounded-md border border-gray-200 text-gray-400 text-sm cursor-not-allowed">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  No Work Order
                </div>
              )
            )}

          </div>
        )}

        {/* SELECTORS */}
        <section className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          {/* Quote Selector */}
          <div className={card}>
            <div className="flex items-center justify-between mb-1">
              <h2 className="text-sm font-bold uppercase tracking-wide text-purple-600">QUOTE</h2>
              {!pendingQuoteMode && (
                <button
                  onClick={() => setPendingQuoteMode(true)}
                  className="text-xs text-amber-600 hover:text-amber-500 transition-colors flex items-center gap-1"
                  title="Track time against a quote without orders"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                  </svg>
                  Pending Quote
                </button>
              )}
            </div>
            <QuoteSelector
              onSelect={handleSelectQuote}
              value={selectedQuote}
              onQuotesLoaded={setAllQuotes}
              pendingQuoteMode={pendingQuoteMode}
            />
          </div>

          {/* Order Selector */}
          <div className={card}>
            <h2 className="text-sm font-bold uppercase tracking-wide text-purple-600 mb-1">ORDER</h2>
            {pendingQuoteMode ? (
              <div className="h-[200px] flex items-center justify-center text-gray-500 text-sm border border-dashed border-gray-300 rounded-lg">
                <div className="text-center">
                  <div className="text-amber-600 font-medium">Pending Quote Mode</div>
                  <div className="text-xs mt-1 text-gray-500">Orders not required</div>
                </div>
              </div>
            ) : (
              <OrderSelector
                quote={selectedQuote}
                selected={selectedOrder}
                onSelect={handleSelectOrder}
              />
            )}
          </div>

          {/* Operation Selector with Reprint Button above */}
          <div className="flex flex-col gap-2">
            {/* Reprint/Remake Button */}
            <button
              onClick={() => setReprintRemakeModal({
                open: true,
                preselectedQuote: selectedQuote?.quoteNumber || null,
                preselectedJobs: selectedJob ? [selectedJob.jobNumber || selectedJob.Job] : null
              })}
              className="flex items-center justify-center gap-2 px-4 py-2.5 bg-gradient-to-r from-amber-400 to-yellow-400 hover:from-amber-500 hover:to-yellow-500 rounded-lg text-amber-900 font-semibold shadow-sm hover:shadow-md transition-all border border-amber-300"
              title="Report a reprint or remake needed"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
              Reprint/Remake
            </button>

            {/* Operation Selector Card */}
            <div className={card}>
              <h2 className="text-sm font-bold uppercase tracking-wide text-purple-600 mb-1">OPERATION</h2>
              <OperationSelector
              order={pendingQuoteMode ? null : selectedOrder}
              onSelect={handleSelectOperation}
              selected={selectedOperation}
              workCenter={selectedWorkCenter}
                showAllOperations={pendingQuoteMode}
              />
            </div>
          </div>
        </section>

        {/* NOTIFICATION ACTION BUTTONS - between selectors and jobs */}
        {!pendingQuoteMode && selectedQuote && (() => {
          const wc = selectedWorkCenter?.toUpperCase();
          const email = userEmail?.toLowerCase();
          const showPA = wc === 'PA' || isPM || isAdmin || email === 'mark.herman@3dcolor.com';
          const showAssembly = wc === 'ASSEMBLY' || wc === 'OPTI' || isAdmin || email === 'mark.herman@3dcolor.com';
          if (!showPA && !showAssembly) return null;
          const needsOrder = !selectedOrder?.orderNumber;
          return (
            <div className="flex flex-wrap items-center gap-2">
              {needsOrder && (
                <span className="text-xs text-amber-600 font-medium mr-1">Select an order first</span>
              )}
              {showPA && (
                <>
                  <button
                    onClick={() => setSubmitProofModal({ open: true, mode: 'pdf-proof' })}
                    disabled={needsOrder}
                    className={`flex items-center gap-1.5 px-4 py-2 rounded-lg text-white font-semibold text-sm shadow-sm transition-all ${
                      needsOrder
                        ? 'bg-gray-300 cursor-not-allowed'
                        : 'bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 hover:shadow-md'
                    }`}
                    title={needsOrder ? "Select an order first" : "Submit PDF proof and notify PM"}
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    Submit PDF Proof
                  </button>
                  <button
                    onClick={() => setSubmitProofModal({ open: true, mode: 'print-finals' })}
                    disabled={needsOrder}
                    className={`flex items-center gap-1.5 px-4 py-2 rounded-lg text-white font-semibold text-sm shadow-sm transition-all ${
                      needsOrder
                        ? 'bg-gray-300 cursor-not-allowed'
                        : 'bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 hover:shadow-md'
                    }`}
                    title={needsOrder ? "Select an order first" : "Submit print finals and notify PM & production"}
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
                    </svg>
                    Submit Print Finals
                  </button>
                </>
              )}
              {showAssembly && (
                <button
                  onClick={() => setSubmitProofModal({ open: true, mode: 'assembly-finals' })}
                  disabled={needsOrder}
                  className={`flex items-center gap-1.5 px-4 py-2 rounded-lg text-white font-semibold text-sm shadow-sm transition-all ${
                    needsOrder
                      ? 'bg-gray-300 cursor-not-allowed'
                      : 'bg-gradient-to-r from-purple-500 to-purple-600 hover:from-purple-600 hover:to-purple-700 hover:shadow-md'
                  }`}
                  title={needsOrder ? "Select an order first" : "Notify PM and production that assembly is complete"}
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  Assembly Finals Completed
                </button>
              )}
            </div>
          );
        })()}

        {/* JOB TRACKER */}
        {!pendingQuoteMode && (
          <section className={card}>
            <h2 className="text-sm font-bold uppercase tracking-wide text-purple-600 mb-2">
              JOBS IN ORDER{' '}
              {selectedOrder?.orderNumber && (
                <span className="text-gray-900 font-normal">#{selectedOrder.orderNumber}</span>
              )}
            </h2>
            <JobTracker
              jobs={jobs}
              selectedOrder={selectedOrder}
              selectedOperation={selectedOperation}
              selectedJob={selectedJob}
              onJobClick={handleJobClick}
              onStartJob={handleStartJob}
              onStartGroupJobs={handleStartGroupJobs}
              onInstantComplete={handleInstantComplete}
              onCompleteGroupJobs={handleCompleteGroupJobs}
              activeSessions={activeSessions}
              onPauseJob={handlePauseJob}
              onResumeJob={handleResumeJob}
              onCompleteJob={handleCompleteJob}
              selectedJobIds={selectedJobIds}
              onSelectionChange={handleSelectionChange}
            />
          </section>
        )}

        {/* TODAY'S SESSIONS */}
        <section className={card}>
          <TodaysSessions />
        </section>

        {/* SESSION HISTORY LINK */}
        <div className="text-center mt-2">
          <button
            onClick={handleViewSessionHistory}
            className="text-sm text-purple-600 hover:text-purple-500 transition-colors underline"
          >
            View My Session History →
          </button>
        </div>
      </div>

      {/* MODALS */}

      {/* Complete Modal */}
      {completeModal.open && (
        <CompleteModal
          job={completeModal.job}
          session={activeSessions[completeModal.job?.id]}
          operation={selectedOperation}
          autoQuantity={completeModal.autoQuantity}
          onCancel={() => setCompleteModal({ open: false, job: null, autoQuantity: false })}
          onSubmit={handleConfirmComplete}
        />
      )}

      {/* Multi-Complete Modal */}
      {multiCompleteModal.open && (
        <MultiCompleteModal
          open={multiCompleteModal.open}
          jobs={multiCompleteModal.jobs}
          operationCode={selectedOperation?.operationCode}
          autoQuantity={multiCompleteModal.autoQuantity}
          onCancel={() => setMultiCompleteModal({ open: false, jobs: [], autoQuantity: false })}
          onSubmit={(data) => {
            // data is { type: "auto-quantity" } or { type: "table", byJob: { [jobId]: { piecesFinished } } }
            if (data.type === 'auto-quantity') {
              // Auto-quantity: use each job's qty
              const quantities = {};
              multiCompleteModal.jobs.forEach(job => {
                quantities[job.id] = job.qty || 0;
              });
              handleConfirmMultiComplete(quantities);
            } else {
              // Table mode: extract piecesFinished for each job
              const quantities = {};
              Object.entries(data.byJob || {}).forEach(([jobId, vals]) => {
                quantities[jobId] = vals.piecesFinished || 0;
              });
              handleConfirmMultiComplete(quantities);
            }
          }}
        />
      )}

      {/* Printer Select Modal */}
      {printerModal.open && (
        <PrinterSelectModal
          job={printerModal.job}
          onCancel={() => setPrinterModal({ open: false, job: null, jobs: [], isMulti: false, printers: [], reprintType: null, reprintReason: null })}
          onSelect={(printer) => {
            const { job, jobs, isMulti, reprintType, reprintReason } = printerModal;
            setPrinterModal({ open: false, job: null, jobs: [], isMulti: false, printers: [], reprintType: null, reprintReason: null });
            if (isMulti && jobs.length > 0) {
              // Multi-job start with printer
              startGroupJobsDirectly(jobs, printer, reprintType, reprintReason);
            } else if (job) {
              // Single job start with printer
              startJobWithPrinter(job, printer);
            }
          }}
        />
      )}

      {/* Reprint Modal */}
      {reprintModal.open && (
        <ReprintModal
          job={reprintModal.job}
          printCount={reprintModal.printCount}
          onCancel={() => setReprintModal({ open: false, job: null, jobs: [], isMulti: false, printCount: 0 })}
          onSelect={(selection) => {
            // selection is { type: "additional"|"reprint", reason: "internal_error"|"customer_request"|null }
            const { job, jobs, isMulti } = reprintModal;
            setReprintModal({ open: false, job: null, jobs: [], isMulti: false, printCount: 0 });

            if (isMulti && jobs.length > 0) {
              // Multi-job: proceed to printer selection with reprint info
              setPrinterModal({
                open: true,
                job: null,
                jobs: jobs,
                isMulti: true,
                printers: PRINTERS,
                reprintType: selection.type,
                reprintReason: selection.reason,
              });
            } else if (job) {
              // Single job: for completion flow (existing behavior)
              handleReprintResponse(selection.type === 'reprint', selection.type, selection.reason);
            }
          }}
        />
      )}

      {/* Rework Cause Modal */}
      {reworkCauseModal.open && (
        <ReworkCauseModal
          operationType={reworkCauseModal.operationType}
          job={reworkCauseModal.job}
          jobs={reworkCauseModal.jobs}
          isMulti={reworkCauseModal.isMulti}
          onSubmit={handleReworkCauseSubmit}
          onCancel={handleReworkCauseCancel}
        />
      )}

      {/* Pending Quote Confirm Modal */}
      {pendingQuoteConfirmModal && (
        <PendingQuoteConfirmModal
          quote={selectedQuote}
          operation={selectedOperation}
          onCancel={() => setPendingQuoteConfirmModal(false)}
          onConfirm={handleConfirmStartPendingQuote}
          isLoading={pendingQuoteStarting}
        />
      )}

      {/* Pending Quote Complete Modal */}
      {pendingQuoteCompleteModal.open && (
        <PendingQuoteCompleteModal
          ticket={pendingQuoteCompleteModal.ticket}
          onCancel={() => setPendingQuoteCompleteModal({ open: false, ticket: null })}
          onComplete={handlePendingQuoteComplete}
        />
      )}

      {/* Reprint/Remake Modal */}
      {reprintRemakeModal.open && (
        <ReprintRemakeModal
          isOpen={reprintRemakeModal.open}
          onClose={() => setReprintRemakeModal({ open: false, preselectedQuote: null, preselectedJobs: null })}
          preselectedQuote={reprintRemakeModal.preselectedQuote}
          preselectedJobs={reprintRemakeModal.preselectedJobs}
          workCenter={selectedWorkCenter}
          currentUser={{ employeeId: accounts[0]?.localAccountId, displayName: accounts[0]?.name }}
          token={sessionStorage.getItem('msal.backendToken')}
        />
      )}

      {/* Rework Start Modal - opens when clicking a reprint task in My Work */}
      {reworkStartModal.open && (
        <ReworkStartModal
          isOpen={reworkStartModal.open}
          task={reworkStartModal.task}
          onClose={() => setReworkStartModal({ open: false, task: null })}
          onStarted={() => {
            // Reload rework queue after starting
            loadReworkQueue(selectedWorkCenter);
          }}
          token={sessionStorage.getItem('msal.backendToken')}
          employeeId={accounts[0]?.localAccountId}
          employeeName={accounts[0]?.name}
        />
      )}

      {/* PM Instant Complete Confirmation Modal */}
      <PMInstantCompleteModal
        open={pmCompleteModal.open}
        job={pmCompleteModal.job}
        jobs={pmCompleteModal.jobs}
        operation={selectedOperation}
        order={selectedOrder}
        onCancel={handleCancelPmComplete}
        onConfirm={handleConfirmPmComplete}
      />

      {/* Submit PDF Proof / Print Finals / Assembly Finals Modal */}
      {submitProofModal.open && (
        <SubmitProofModal
          isOpen={submitProofModal.open}
          onClose={() => setSubmitProofModal({ open: false, mode: 'pdf-proof' })}
          quoteNumber={selectedQuote?.quoteNumber}
          orderNumber={
            // Only pass the order when it belongs to the selected quote. A stale
            // cross-quote selection must never be sent — the backend also blocks
            // it, but not sending it at all avoids a confusing round-trip error.
            selectedOrder &&
            String(selectedOrder.quoteNumber) === String(selectedQuote?.quoteNumber)
              ? selectedOrder.orderNumber || selectedOrder.Order
              : undefined
          }
          projectName={selectedQuote?.user_Text4 || selectedOrder?.projectName || ''}
          customerName={selectedQuote?.customerName || ''}
          pmName={selectedOrder?.projectManager || selectedOrder?.user_Text1 || selectedQuote?.user_Text1 || ''}
          jobs={jobs}
          mode={submitProofModal.mode}
          token={sessionStorage.getItem('msal.backendToken')}
        />
      )}

      {/* FIXED JOB DETAIL DRAWER - positioned below TopNav and SubNav */}
      <div className="fixed right-0 top-28 h-[calc(100vh-7rem)] z-[60]">
        <JobDetailDrawer
          job={selectedJob}
          operation={selectedOperation}
          selectedRoutingStep={selectedRoutingStep}
          routing={routing}
          routingLoading={routingLoading}
          routingError={routingError}
          activeSessions={activeSessions}
          onRoutingStepSelect={handleRoutingStepSelect}
          onClose={() => {
            setSelectedJob(null);
            setSelectedRoutingStep(null);
          }}
          onStartJob={handleStartJob}
          onPauseJob={handlePauseJob}
          onResumeJob={handleResumeJob}
          onCompleteJob={handleCompleteJob}
        />
      </div>
    </div>
  );
}
